import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Collapse,
  Chip,
  Grid
} from "@mui/material";
import PlayerHeadshot from "./PlayerHeadshot";
import { getTeamExternalId } from "src/utils/matchDetailsUtils";
import { getPositionLetter } from "src/utils/positionUtils";

const getMatchStatKeys = (sportType) => {
  switch (sportType) {
    case 'soccer':
      return ['Goals', 'GoalsAgainst', 'Saves', 'Corners', 'Shutouts'];
    case 'football':
      return ['Touchdowns', 'TotalYards', 'PassingYards', 'RushingYards', 'FirstDowns', 'Sacks', 'DefensiveInterceptions', 'FieldGoalsMade'];
    case 'basketball':
      return ['Points', 'Rebounds', 'Assists', 'Steals', 'Blocks'];
    case 'australian_football':
      return ['Goals', 'Tackles', 'Marks', 'Kicks', 'Handballs'];
    case 'rugby':
      return ['Tries', 'Tackles', 'LineBreaks', 'Goals'];
    case 'cricket':
      return ['Runs', 'Wickets', 'Sixes', 'Fours'];
    case 'hockey':
    default:
      return ['Goals', 'GoalsAgainst', 'Saves', 'PowerPlayGoals', 'ShortHandedGoals'];
  }
}

const getPlayerStatKeys = (sportType) => {
  switch (sportType) {
    case 'soccer':
      return ['Goals', 'Assists', 'ShotsOnGoal', 'Tackles', 'PassInterceptions'];
    case 'football':
      return ['PassingYards', 'PassingTouchdowns', 'RushingYards', 'RushingTouchdowns', 'Receptions', 'ReceivingYards', 'ReceivingTouchdowns', 'InterceptionsThrown', 'FumblesLost'];
    case 'basketball':
      return ['Points', 'Rebounds', 'Assists', 'Steals', 'Blocks'];
    case 'australian_football':
      return ['Kicks', 'Handballs', 'Tackles', 'Hitouts', 'Goals', 'FreeKickAgainst'];
    case 'rugby':
      return ['Tries', 'TryAssists', 'LineBreaks', 'Tackles', 'Goals'];
    case 'cricket':
      return ['Runs', 'Fours', 'Sixes', 'Wickets', 'ThreeWickets', 'FiftyPlusRuns'];
    case 'hockey':
    default:
      return ['Goals', 'Assists', 'ShotsOnGoal', 'Hits', 'BlockedShots'];
  }
}
const PlayerStatsTableHeader = ({ sportType }) => {
  switch (sportType) {
    case 'soccer':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">G</TableCell>
          <TableCell align="center">A</TableCell>
          <TableCell align="center">SOG</TableCell>
          <TableCell align="center">T</TableCell>
          <TableCell align="center">PI</TableCell>
        </TableRow>
      );
    case 'football':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">PA YDS</TableCell>
          <TableCell align="center">PA TD</TableCell>
          <TableCell align="center">RU YDS</TableCell>
          <TableCell align="center">RU TD</TableCell>
          <TableCell align="center">REC</TableCell>
          <TableCell align="center">RE YDS</TableCell>
          <TableCell align="center">RE TD</TableCell>
          <TableCell align="center">INT</TableCell>
          <TableCell align="center">FL</TableCell>
        </TableRow>
      );
    case 'basketball':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">PTS</TableCell>
          <TableCell align="center">REB</TableCell>
          <TableCell align="center">AST</TableCell>
          <TableCell align="center">STL</TableCell>
          <TableCell align="center">BLK</TableCell>
        </TableRow>
      );
    case 'australian_football':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">K</TableCell>
          <TableCell align="center">HB</TableCell>
          <TableCell align="center">T</TableCell>
          <TableCell align="center">HO</TableCell>
          <TableCell align="center">G</TableCell>
          <TableCell align="center">FKA</TableCell>
        </TableRow>
      );
    case 'rugby':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">T</TableCell>
          <TableCell align="center">TA</TableCell>
          <TableCell align="center">LB</TableCell>
          <TableCell align="center">TK</TableCell>
          <TableCell align="center">G</TableCell>
        </TableRow>
      );
    case 'cricket':
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">R</TableCell>
          <TableCell align="center">4s</TableCell>
          <TableCell align="center">6s</TableCell>
          <TableCell align="center">W</TableCell>
          <TableCell align="center">3W</TableCell>
          <TableCell align="center">50+ R</TableCell>
        </TableRow>
      );
    case 'hockey':
    default:
      return (
        <TableRow>
          <TableCell>Player</TableCell>
          <TableCell align="center">G</TableCell>
          <TableCell align="center">A</TableCell>
          <TableCell align="center">SOG</TableCell>
          <TableCell align="center">HIT</TableCell>
          <TableCell align="center">BLK</TableCell>
        </TableRow>
      );
  }
};

const MatchDetailsDialog = ({ open, onClose, matchData, sportType }) => {

  const [showRawData, setShowRawData] = useState(false);

  if (!matchData) return null;

  const { showMatch } = matchData;
  console.log("MatchDetailsDialog sportType", sportType);
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Match Details</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {showMatch && showMatch.details && showMatch.stats && (
            <>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
                {/* Home Team */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <PlayerHeadshot
                      row={{ externalRef: getTeamExternalId(showMatch.details, 'Home') }}
                      imageHash={showMatch.details.HomeTeamName}
                    />
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {showMatch.details.HomeTeamName}
                    </Typography>
                  </Box>
                </Grid>

                {/* Center - Score and Status */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Chip
                      label={showMatch.status}
                      size="small"
                      sx={{
                        backgroundColor: (() => {
                          const status = showMatch.status?.toLowerCase();
                          if (status === 'live' || status === 'inprogress') return '#00BC6D';
                          if (status === 'upcoming' || status === 'scheduled') return '#FF8423';
                          if (status === 'finished' || status === 'completed') return '#AAAAAA';
                          return '#1976d2';
                        })(),
                        color: '#fff'
                      }}
                    />

                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h3" component="span">
                        {showMatch.details.HomeTeamScore}
                      </Typography>
                      <Typography variant="h3" component="span" sx={{ mx: 1 }}>
                        :
                      </Typography>
                      <Typography variant="h3" component="span">
                        {showMatch.details.AwayTeamScore}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {showMatch.details.Minute || "--"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {showMatch.details.Period}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Away Team */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <PlayerHeadshot
                      row={{ externalRef: getTeamExternalId(showMatch.details, 'Away') }}
                      imageHash={showMatch.details.AwayTeamName}
                    />
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {showMatch.details.AwayTeamName}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {showMatch?.stats && showMatch?.details && (
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  const teamStats = showMatch.stats;
                  const home = teamStats.find(
                    (t) => t.TeamFeedId === showMatch.details.HomeTeamFeedId
                  );
                  const away = teamStats.find(
                    (t) => t.TeamFeedId === showMatch.details.AwayTeamFeedId
                  );

                  const statKeys = getMatchStatKeys(sportType);

                  return statKeys.map((key) => {
                    const homeValue = home?.[key] ?? 0;
                    const awayValue = away?.[key] ?? 0;
                    // Scale each row's bars relative to the larger side, so
                    // count stats and yardage stats both render meaningfully
                    const rowMax = Math.max(homeValue, awayValue, 1);
                    const getProgress = (val) => Math.min((val / rowMax) * 100, 100);

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ width: "40%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={getProgress(homeValue)}
                            sx={{
                              height: 10,
                              backgroundColor: "#eee",
                              borderRadius: 2,
                              transform: "rotate(180deg)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "red",
                              },
                            }}
                          />
                        </Box>
                        <Typography width="30px" textAlign="center">
                          {homeValue}
                        </Typography>

                        <Typography sx={{ minWidth: 170, textAlign: "center" }}>
                          {key}
                        </Typography>

                        <Typography width="30px" textAlign="center">
                          {awayValue}
                        </Typography>
                        <Box sx={{ width: "40%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={getProgress(awayValue)}
                            sx={{
                              height: 10,
                              backgroundColor: "#eee",
                              borderRadius: 2,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "black",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 4 }}>
            {showMatch?.stats?.map((teamStats, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {teamStats.TeamFeedId === showMatch.details.HomeTeamFeedId
                    ? showMatch.details.HomeTeamName
                    : showMatch.details.AwayTeamName}
                </Typography>

                <Table size="small">
                  <TableHead>
                    <PlayerStatsTableHeader sportType={sportType} />
                  </TableHead>
                  <TableBody>
                    {teamStats.Players?.map((player, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {(() => {
                              const playerName =
                                player.PlayerName || `Player ${index + 1}`;
                              const nameParts = playerName.split(" ");
                              const position = player.PlayerScoreablePosition ?? null;
                              const positionLetter = position !== null ? getPositionLetter(position) : "";

                              let formattedName;
                              if (nameParts.length >= 2) {
                                const firstName = nameParts[0];
                                const lastName = nameParts.slice(1).join(" ");
                                formattedName = `${firstName.charAt(0)}. ${lastName}`;
                              } else {
                                formattedName = playerName;
                              }

                              return (
                                <>
                                  {positionLetter && (
                                    <Chip
                                      label={positionLetter}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        minWidth: 30,
                                        backgroundColor: '#1976d2',
                                        color: '#fff'
                                      }}
                                    />
                                  )}
                                  <Typography variant="body2">{formattedName}</Typography>
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        {getPlayerStatKeys(sportType).map((statKey) => (
                          <TableCell align="center" key={statKey}>
                            {sportType === 'cricket' && statKey === 'ThreeWickets'
                              ? player.Wickets >= 3 ? 1 : 0
                              : sportType === 'cricket' && statKey === 'FiftyPlusRuns'
                                ? player.Runs >= 50 ? 1 : 0
                                : player[statKey] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            ))}
          </Grid>
        </DialogContentText>

        {showMatch !== null && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRawData(!showRawData)}
              sx={{ mb: 2 }}
            >
              {showRawData ? "Hide Raw Data" : "Show Raw Data"}
            </Button>
            <Collapse in={showRawData}>
              <DialogContentText id="alert-dialog-description">
                <div>
                  <pre>{JSON.stringify(showMatch.stats, null, 2)}</pre>
                </div>
              </DialogContentText>
            </Collapse>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchDetailsDialog;
